"use client"

import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useFocusTrap } from "./hooks/useFocusTrap"
import { usePopupStore } from "./store/usePopupStore"

export const PopupPortalContent = ({
  isOpen,
  position,
  dimmer = "default",
  ariaLabel,
  children,
  onClose,
}: {
  isOpen: boolean
  position: "anchor-top" | null
  dimmer?: "default" | "strong"
  ariaLabel?: string
  children: React.ReactNode
  onClose?: () => void
}) => {
  const { containerRef, handleAnimationComplete, refocus } = useFocusTrap({
    isOpen,
    onClose,
  })

  // isOpen 유지된 상태에서 content가 교체되는 경우 포커스 재설정
  // (예: closePopup() → openPopup(새 내용) 동일 이벤트 핸들러, 글로벌팝업 큐)
  const prevChildrenRef = useRef<React.ReactNode>(undefined)
  useEffect(() => {
    if (
      isOpen &&
      prevChildrenRef.current !== undefined &&
      prevChildrenRef.current !== children
    ) {
      refocus()
    }
    prevChildrenRef.current = children
  }, [isOpen, children, refocus])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`pointer-events-auto fixed inset-0 z-10000 flex w-full items-center justify-center ${
            dimmer === "strong" ? "bg-black/60" : "bg-black/40"
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            ref={containerRef}
            role="dialog"
            aria-label={ariaLabel}
            aria-modal="true"
            tabIndex={-1}
            className={
              position == "anchor-top"
                ? `flex h-full w-full flex-col items-center pt-[148px]`
                : `flex h-full w-full items-center justify-center`
            }
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onAnimationComplete={handleAnimationComplete}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export const createPopupPortal = ({
  elementId,
  isOpen,
  position,
  dimmer,
  ariaLabel,
  children,
  onClose,
}: {
  elementId: string
  isOpen: boolean
  position: "anchor-top" | null
  dimmer?: "default" | "strong"
  ariaLabel?: string
  children: React.ReactNode
  onClose?: () => void
}) => {
  const overlayRoot = document.getElementById(elementId)
  return createPortal(
    <PopupPortalContent
      isOpen={isOpen}
      position={position}
      dimmer={dimmer}
      ariaLabel={ariaLabel}
      onClose={onClose}
    >
      {children}
    </PopupPortalContent>,
    overlayRoot as Element,
  )
}

export default function PopupPortal() {
  const elementId = "overlay-root"
  const { isOpen, content, position, dimmer, ariaLabel, closePopup } =
    usePopupStore()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  if (!mounted) return null

  const overlayRoot = document.getElementById(elementId)

  if (!overlayRoot) return null

  return createPopupPortal({
    elementId,
    isOpen,
    position,
    dimmer,
    children: content,
    ariaLabel,
    onClose: closePopup,
  })
}
